import { useCallback, useState, useEffect } from 'react'
import { message } from 'antd'

type MSRESPONSE = {
  success: boolean
  [key: string]: any
}

const fetchRequest = async (input: RequestInfo, init?: RequestInit | undefined): Promise<MSRESPONSE> => {
  const res: MSRESPONSE = await fetch(input, init).then((res) => {
    if (res.status === 401) {
      window.location.href =
        `${process.env.REACT_APP_MSGRAPH}/auth/signin?from=` + window.location.pathname + window.location.search
    }
    return res.json()
  })

  if (!res.success) {
    throw new Error(JSON.stringify(res))
  }

  return res
}

const MsGraphAPI = function () {
  const token = 'Bearer ' + localStorage.getItem('jwt')
  const defaultOptions: RequestInit = {
    mode: 'cors',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
  }

  return {
    connect: () =>
      fetch(`${process.env.REACT_APP_MSGRAPH}/auth/`, {
        method: 'GET',
        ...defaultOptions,
      }),
    sendEmail: async ({ data }: { data: any }) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/`, {
        method: 'POST',
        body: JSON.stringify({ data }),
        ...defaultOptions,
      }),
    sendEmailWithAttachment: async ({ data, attachment }: { data: any; attachment: any }) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message?withAttachment=1`, {
        method: 'POST',
        body: JSON.stringify({ data, attachment }),
        ...defaultOptions,
      }),
    getMessages: async () =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/`, {
        method: 'GET',
        ...defaultOptions,
      }),
    archiver: async (messageId: string, folder: string) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/${messageId}/move`, {
        method: 'POST',
        body: JSON.stringify({ folder }),
        ...defaultOptions,
      }),
    reply: async (messageId: string, data: any) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/${messageId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ data }),
        ...defaultOptions,
      }),
    forward: async (messageId: string, data: any) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/${messageId}/forward`, {
        method: 'POST',
        body: JSON.stringify({ data }),
        ...defaultOptions,
      }),
    attachments: async (messageId: string) =>
      await fetchRequest(`${process.env.REACT_APP_MSGRAPH}/message/${messageId}/attachments`, {
        method: 'POST',
        ...defaultOptions,
      }),
  }
}

const instance = MsGraphAPI()

export const useArchiveMessage = (): [(id: string) => Promise<boolean>, boolean] => {
  const [loading, setLoading] = useState(false)

  const archiveMessage = useCallback(async (messageId) => {
    setLoading(true)
    const res = await instance.archiver(messageId, 'Archives Vision')
    if (res.success) {
      setLoading(false)
      message.success('Message archivé')
      return Promise.resolve(true)
    }
    message.error("Erreur lors de l'archivage du message")
    setLoading(false)
    return Promise.resolve(false)
  }, [])

  return [archiveMessage, loading]
}

export const useFetchAttachments = (): [(id: string) => Promise<{ value: [] }>, boolean] => {
  const [loading, setLoading] = useState(false)

  const fetchAttachments = useCallback(async (messageId) => {
    setLoading(true)
    const res = await instance.attachments(messageId)
    if (res.success) {
      setLoading(false)
      return Promise.resolve(res.attachments)
    }
    message.error('Erreur lors de la récupération des pièces jointes')
    setLoading(false)
    return Promise.resolve(null)
  }, [])

  return [fetchAttachments, loading]
}

export const useMsGraphAPI = () => {
  const connect = async () => {
    await MsGraphAPI()
      .connect()
      .then(async (res: Response) => {
        if (res.status === 401) {
          window.location.href =
            `${process.env.REACT_APP_MSGRAPH}/auth/signin?from=` + window.location.pathname + window.location.search
        }
        return res.json()
      })
  }

  useEffect(() => {
    connect()
  }, [])

  return instance
}

export default instance
